import queryString from "qs";
import { uniqBy, clone, camelCase, forEach } from "lodash-es";
import {
  getFieldsMappedByCCDisplayName,
  tableQueryParamsToHasuraClauses
} from "./tableQueryParamsToHasuraClauses";
import { filterLocalEntitiesToHasura } from "./filterLocalEntitiesToHasura";
import { initializeHasuraWhereAndFilter } from "./initializeHasuraWhereAndFilter";
const defaultPageSizes = [5, 10, 15, 25, 50, 100, 200, 400];
export { defaultPageSizes };
export function getMergedOpts(topLevel = {}, instanceLevel = {}) {
  const merged = {
    ...topLevel,
    ...instanceLevel
  };
  return {
    formName: "tgDataTable",
    ...merged,
    pageSize: merged.controlled_pageSize || merged.pageSize,
    defaults: {
      pageSize: merged.controlled_pageSize || 25,
      order: [],
      //[-name, statusCode] //an array of camelCase display names with - sign to denote reverse
      searchTerm: "",
      page: 1,
      filters: [
        //filters look like this:
        // {
        //   selectedFilter: 'textContains', //camel case
        //   filterOn: ccDisplayName, //camel case display name if available and string, otherwise path
        //   filterValue: 'thomas',
        // }
      ],
      ...topLevel.defaults || {},
      ...instanceLevel.defaults || {}
    }
  };
}
function safeStringify(val) {
  if (val !== null && typeof val === "object") {
    return JSON.stringify(val);
  }
  return val;
}
function safeParse(val) {
  try {
    return JSON.parse(val);
  } catch (e) {
    return val;
  }
}
export function getCurrentParamsFromUrl(location, isSimple) {
  let { search } = location;
  if (isSimple) {
    search = window.location.href.split("?")[1] || "";
  }
  return parseFilters(queryString.parse(search, { ignoreQueryPrefix: true }));
}
export function setCurrentParamsOnUrl(newParams, replace, isSimple) {
  const stringifiedFilters = stringifyFilters(newParams);
  const search = `?${queryString.stringify(stringifiedFilters)}`;
  if (isSimple) {
    return window.location.replace(
      `${window.location.href.split("?")[0]}${search}`
    );
  }
  replace({
    search
  });
}
function stringifyFilters(newParams) {
  let filters;
  if (newParams.filters && newParams.filters.length) {
    filters = newParams.filters.reduce(
      (acc, { filterOn, selectedFilter, filterValue }, index) => {
        acc += (index > 0 ? "::" : "") + `${filterOn}__${camelCase(selectedFilter)}__${safeStringify(
          Array.isArray(filterValue) ? filterValue.join(";") : filterValue
        )}`;
        return acc;
      },
      ""
    );
  }
  let order;
  if (newParams.order && newParams.order.length) {
    order = newParams.order.reduce((acc, order2, index) => {
      acc += (index > 0 ? "___" : "") + order2;
      return acc;
    }, "");
  }
  return {
    ...newParams,
    filters,
    order
  };
}
function parseFilters(newParams) {
  return {
    ...newParams,
    order: newParams.order && newParams.order.split("___"),
    filters: newParams.filters && newParams.filters.split("::").map((filter) => {
      const splitFilter = filter.split("__");
      const [filterOn, selectedFilter, filterValue] = splitFilter;
      const parseFilterValue = (filterValue2) => {
        if (selectedFilter === "inList" || selectedFilter === "notInList") {
          return filterValue2.split(";");
        }
        if (selectedFilter === "inRange" || selectedFilter === "outsideRange") {
          return filterValue2.split(";").map(Number);
        }
        return safeParse(filterValue2);
      };
      return {
        filterOn,
        selectedFilter,
        filterValue: parseFilterValue(filterValue)
      };
    })
  };
}
export function makeDataTableHandlers({
  setNewParams,
  defaults,
  onlyOneFilter
}) {
  const setSearchTerm = (searchTerm) => {
    setNewParams((prev) => ({
      ...prev ?? {},
      page: void 0,
      //set page undefined to return the table to page 1
      searchTerm: searchTerm === defaults.searchTerm ? void 0 : searchTerm
    }));
    onlyOneFilter && clearFilters();
  };
  const addFilters = (newFilters) => {
    if (!newFilters) return;
    setNewParams((prev) => {
      const filters = uniqBy(
        [...newFilters, ...onlyOneFilter ? [] : prev?.filters || []],
        "filterOn"
      );
      return {
        ...prev ?? {},
        page: void 0,
        //set page undefined to return the table to page 1
        filters
      };
    });
  };
  const removeSingleFilter = (filterOn) => setNewParams((prev) => {
    const filters = prev?.filters ? prev.filters.filter((filter) => {
      return filter.filterOn !== filterOn;
    }) : void 0;
    return { ...prev ?? {}, filters };
  });
  const clearFilters = (additionalFilterKeys = []) => {
    const toClear = {
      filters: void 0,
      searchTerm: void 0,
      tags: void 0
    };
    additionalFilterKeys.forEach((key) => {
      toClear[key] = void 0;
    });
    setNewParams(toClear);
  };
  const setPageSize = (pageSize) => setNewParams((prev) => ({
    ...prev ?? {},
    pageSize: pageSize === defaults.pageSize ? void 0 : pageSize,
    page: void 0
    //set page undefined to return the table to page 1
  }));
  const setOrder = (order, isRemove, shiftHeld) => setNewParams((prev) => {
    let newOrder = [];
    if (shiftHeld) {
      newOrder = [...prev?.order || []].filter((value) => {
        const shouldRemove = value.replace(/^-/, "") === order.replace(/^-/, "");
        return !shouldRemove;
      });
      if (!isRemove) {
        newOrder.push(order);
      }
    } else {
      if (isRemove) {
        newOrder = [];
      } else {
        newOrder = [order];
      }
    }
    return {
      ...prev ?? {},
      order: newOrder
    };
  });
  const setPage = (page) => {
    setNewParams((prev) => ({
      ...prev ?? {},
      page: page === defaults.page ? void 0 : page
    }));
  };
  return {
    setSearchTerm,
    addFilters,
    clearFilters,
    removeSingleFilter,
    setPageSize,
    setPage,
    setOrder,
    setNewParams
  };
}
function cleanupFilters({ filters, ccFields }) {
  (filters || []).forEach((filter) => {
    const { filterOn, filterValue } = filter;
    const field = ccFields[filterOn];
    if (field.type === "number" || field.type === "integer") {
      filter.filterValue = Array.isArray(filterValue) ? filterValue.map((val) => Number(val)) : Number(filterValue);
    }
    if (filter.selectedFilter === "inList" && typeof filter.filterValue === "number") {
      filter.filterValue = filter.filterValue.toString();
    }
  });
}
export function getQueryParams({
  currentParams,
  urlConnected,
  defaults,
  schema,
  isInfinite,
  entities,
  isLocalCall,
  additionalFilter,
  doNotCoercePageSize,
  noOrderError,
  isCodeModel,
  ownProps
}) {
  let errorParsingUrlString;
  try {
    Object.keys(currentParams).forEach(function(key) {
      if (currentParams[key] === void 0) {
        delete currentParams[key];
      }
    });
    const tableQueryParams = {
      ...defaults,
      ...currentParams
    };
    let { page, pageSize, searchTerm, filters, order } = tableQueryParams;
    const ccFields = getFieldsMappedByCCDisplayName(schema);
    cleanupFilters({ filters, ccFields });
    if (page <= 0 || isNaN(page)) {
      page = void 0;
    }
    if (isInfinite) {
      page = void 0;
      pageSize = void 0;
    }
    if (pageSize !== void 0 && !doNotCoercePageSize) {
      const closest = clone(window.tgPageSizes || defaultPageSizes).sort(
        (a, b) => Math.abs(pageSize - a) - Math.abs(pageSize - b)
      )[0];
      pageSize = closest;
    }
    const cleanedOrder = [];
    if (order && order.length) {
      order.forEach((orderVal) => {
        const ccDisplayName = orderVal.replace(/^-/gi, "");
        const schemaForField = ccFields[ccDisplayName];
        if (schemaForField) {
          const { path, additionalSearchPaths } = schemaForField;
          const reversed = ccDisplayName !== orderVal;
          const prefix = reversed ? "-" : "";
          cleanedOrder.push(prefix + path);
          if (additionalSearchPaths && additionalSearchPaths.length) {
            additionalSearchPaths.forEach((additionalPath) => {
              cleanedOrder.push(prefix + additionalPath);
            });
          }
        } else {
          !noOrderError && console.error(
            "No schema for field found!",
            ccDisplayName,
            JSON.stringify(schema.fields, null, 2)
          );
        }
      });
    }
    let toRet = {
      //these are values that might be generally useful for the wrapped component
      page,
      pageSize: ownProps.controlled_pageSize || pageSize,
      order: cleanedOrder,
      filters,
      searchTerm
    };
    const { where, order_by, limit, offset } = tableQueryParamsToHasuraClauses({
      page,
      pageSize,
      searchTerm,
      filters,
      order: cleanedOrder,
      schema
    });
    initializeHasuraWhereAndFilter(additionalFilter, where, currentParams);
    if (isLocalCall) {
      const r = filterLocalEntitiesToHasura(
        prepEntitiesForLocalFilter({ entities, ccFields }),
        {
          where,
          order_by: (Array.isArray(order_by) ? order_by : [order_by]).map(
            (obj) => {
              const path = Object.keys(obj)[0];
              return {
                path,
                direction: obj[path],
                ownProps,
                ...ccFields[path]
              };
            }
          ),
          limit,
          offset,
          isInfinite
        }
      );
      toRet = {
        ...toRet,
        ...r
      };
      return toRet;
    } else {
      if (!order_by.length) {
        order_by.push({ updatedAt: "desc" });
      }
      order_by.push(
        isCodeModel ? { code: "asc" } : { [window.__sortId || "id"]: "asc" }
      );
      return {
        ...toRet,
        variables: {
          where,
          order_by,
          limit,
          offset
        }
      };
    }
  } catch (e) {
    if (urlConnected) {
      errorParsingUrlString = e;
      console.error(
        "The following error occurred when trying to build the query params. This is probably due to a malformed URL:",
        e
      );
      return {
        errorParsingUrlString,
        variables: {
          where: {},
          order_by: [],
          limit: 0,
          offset: 0
        }
      };
    } else {
      console.error("Error building query params from filter:");
      throw e;
    }
  }
}
function prepEntitiesForLocalFilter({ entities, ccFields }) {
  const r = entities.map((entity) => {
    const toSpread = {};
    let hasChanged = false;
    forEach(ccFields, ({ getValueToFilterOn, path }) => {
      if (getValueToFilterOn) {
        hasChanged = true;
        toSpread["___original___" + path] = entity[path];
        const value = getValueToFilterOn(entity);
        toSpread[path] = value;
      }
    });
    if (hasChanged) {
      return { ...entity, ...toSpread };
    }
    return entity;
  });
  return r;
}
